/**
 * CurrencyContext — Stage 2 Agent B wiring.
 *
 * Provides tenant-scoped currency state to the React tree so any
 * component can render amounts in the tenant's default currency
 * without prop-drilling. Persists changes through the new dedicated
 * PATCH /tenants/me/currency endpoint.
 *
 * Supported currencies match the W5 Currency Service catalogue
 * (14 codes, Arabic + English locale formatting).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { api } from '@/lib/api'

export type SupportedCurrency =
  | 'AED'
  | 'SAR'
  | 'EGP'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'JPY'
  | 'CNY'
  | 'INR'
  | 'PKR'
  | 'BDT'
  | 'KWD'
  | 'QAR'
  | 'BHD'

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  'AED', 'SAR', 'EGP', 'USD', 'EUR', 'GBP',
  'JPY', 'CNY', 'INR', 'PKR', 'BDT',
  'KWD', 'QAR', 'BHD',
]

export const CURRENCY_LABELS: Record<SupportedCurrency, string> = {
  AED: 'UAE Dirham (د.إ)',
  SAR: 'Saudi Riyal (﷼)',
  EGP: 'Egyptian Pound (ج.م)',
  USD: 'US Dollar ($)',
  EUR: 'Euro (€)',
  GBP: 'British Pound (£)',
  JPY: 'Japanese Yen (¥)',
  CNY: 'Chinese Yuan (¥)',
  INR: 'Indian Rupee (₹)',
  PKR: 'Pakistani Rupee (₨)',
  BDT: 'Bangladeshi Taka (৳)',
  KWD: 'Kuwaiti Dinar (د.ك)',
  QAR: 'Qatari Riyal (﷼)',
  BHD: 'Bahraini Dinar (.د.ب)',
}

const ARABIC_LOCALE_CURRENCIES = new Set<SupportedCurrency>([
  'AED', 'SAR', 'EGP', 'KWD', 'QAR', 'BHD',
])

export const DEFAULT_CURRENCY: SupportedCurrency = 'AED'

export function isSupportedCurrency(code: string | null | undefined): code is SupportedCurrency {
  return !!code && (SUPPORTED_CURRENCIES as string[]).includes(code)
}

export interface CurrencyContextValue {
  currency: SupportedCurrency
  setCurrency: (c: SupportedCurrency) => Promise<void>
  format: (amount: number, opts?: { compact?: boolean; currency?: SupportedCurrency }) => string
  isLoading: boolean
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null)

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<SupportedCurrency>(DEFAULT_CURRENCY)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    api
      .get<{ default_currency_code?: string }>('/tenants/me')
      .then((res) => {
        if (cancelled) return
        const code = res.data?.default_currency_code
        if (isSupportedCurrency(code)) setCurrencyState(code)
      })
      .catch(() => {
        // Anonymous / unauthenticated callers fall back to AED.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const setCurrency = useCallback(async (next: SupportedCurrency) => {
    setCurrencyState(next)
    try {
      await api.patch('/tenants/me/currency', { currency_code: next })
    } catch {
      // Persistence failure leaves the in-memory value optimistic; the
      // caller (Settings page) shows its own error toast.
    }
  }, [])

  const format = useCallback(
    (amount: number, opts?: { compact?: boolean; currency?: SupportedCurrency }) => {
      const target = opts?.currency ?? currency
      const locale = ARABIC_LOCALE_CURRENCIES.has(target) ? 'ar' : 'en'
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: target,
        notation: opts?.compact ? 'compact' : 'standard',
      }).format(amount)
    },
    [currency],
  )

  const value = useMemo<CurrencyContextValue>(
    () => ({ currency, setCurrency, format, isLoading }),
    [currency, setCurrency, format, isLoading],
  )

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext)
  if (!ctx) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return ctx
}
